import React, { createContext, useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { FC, ReactNode } from 'react';
import type { User } from '../@types/user';
import SplashScreen from '../components/SplashScreen';
import firebase from '../lib/firebase';
import {
  authStateChanged,
  userDataRequestInit,
  clearUserAuth,
  empresaCleanListEmps
} from '../redux/actions';
import { RootState } from '../redux/types';
import { useSnackbar } from 'notistack';
import { throttle } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@material-ui/core';
import { tempoInatividade, tempoLimitAlert } from 'src/config';
import { closeAllRegisteredSessions } from 'src/hooks/sessionRegistry';
import { closeSessionLogin } from 'src/integracoes/servicos/mongo-atlas/funcoes/sessionLogin';

interface AuthState {
  isInitialised: boolean;
  isAuthenticated: boolean;
  user: User | null;
}

const TEMPO_INATIVIDADE = Number(tempoInatividade) * 60 * 1000; // ex: 30 minutos
const TEMPO_ALERTA = Number(tempoLimitAlert); // ex: 40 segundos de contador
const CHECK_INACTIVITY_INTERVAL = 20000; // 20 segundos para verificação

export interface AuthContextValue extends AuthState {
  method: 'FirebaseAuth';
  createUserWithEmailAndPassword: (
    email: string,
    password: string
  ) => Promise<firebase.auth.UserCredential>;
  signInWithEmailAndPassword: (
    email: string,
    password: string
  ) => Promise<firebase.auth.UserCredential>;
  signInWithGoogle: () => Promise<firebase.auth.UserCredential>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  verifyPasswordResetCode: (code: string) => Promise<string>;
  confirmPasswordReset: (code: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  isInitialised: false,
  user: null,
  method: 'FirebaseAuth',
  createUserWithEmailAndPassword: () => Promise.resolve({} as firebase.auth.UserCredential),
  signInWithEmailAndPassword: () => Promise.resolve({} as firebase.auth.UserCredential),
  signInWithGoogle: () => Promise.resolve({} as firebase.auth.UserCredential),
  sendPasswordResetEmail: () => Promise.resolve(),
  verifyPasswordResetCode: () => Promise.resolve(''),
  confirmPasswordReset: () => Promise.resolve(),
  logout: () => Promise.resolve()
});

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const account = useSelector((state: RootState) => state.account);
  const { enqueueSnackbar } = useSnackbar();

  const [showTimeoutDialog, setShowTimeoutDialog] = useState(false);
  const [countdown, setCountdown] = useState(TEMPO_ALERTA);
  const [countdownInterval, setCountdownInterval] = useState<NodeJS.Timeout | null>(null);

  const logoutScheduledAt = useRef<number | null>(null);

  // ================================
  // Controle de Inatividade
  // ================================

  /* Atualiza o timestamp da última atividade do usuário com throttle */
  const updateLastActivity = useMemo(
    () =>
      throttle(
        () => {
          localStorage.setItem('lastActivity', Date.now().toString());
          // Sincroniza atividade com outras abas para que elas também resetem o timer
          localStorage.setItem('activitySync', Date.now().toString());
        },
        2000,
        { leading: true, trailing: true }
      ),
    []
  );

  /* Remove chaves de controle de abas de sessão do localStorage. */
  const limparListadeAbas = () => {
    const keysToRemove = Object.keys(localStorage).filter((key) =>
      key.startsWith('session_abas_') ||
      key.startsWith('session_pulse_') ||
      key.startsWith('login_tabs_') ||
      key.startsWith('login_session_started_')
    );
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    localStorage.removeItem('session_ended');
  };

  /* Executa o processo completo de logout. */
  const logout = async () => {
    // Fecha todas as sessões ativas ANTES do signOut para garantir que
    // o token Firebase ainda é válido no momento das chamadas à API.
    // pega o deviceId único desta aba/navegador
    const deviceIdKey = 'device_session_id';
    let deviceId = localStorage.getItem(deviceIdKey);
    if (!deviceId) {
      deviceId = uuidv4();
      localStorage.setItem(deviceIdKey, deviceId);
    }
    await closeAllRegisteredSessions();
    if (account.user?.id) {
      await closeSessionLogin({ entityType: 'login', entityId: account.user.id, deviceId: deviceId as string });
    }
    limparListadeAbas();
    // Usa timestamp para garantir que o storage event dispare sempre (valor muda)
    localStorage.setItem('logoutSignal', Date.now().toString());

    dispatch(clearUserAuth());
    dispatch(empresaCleanListEmps());
    await firebase.auth().signOut();
  };

  /* Inicia o contador regressivo do modal de expiração.*/
  const startCountdown = (externalLogoutAt?: number) => {
    // Evita iniciar múltiplos contadores se já houver um agendado localmente
    if (logoutScheduledAt.current && !externalLogoutAt) return;

    const logoutAt = externalLogoutAt || (Date.now() + TEMPO_ALERTA * 1000);
    logoutScheduledAt.current = logoutAt;

    // Se iniciado localmente, avisa as outras abas
    if (!externalLogoutAt) {
      localStorage.setItem('countdownStart', logoutAt.toString());
    }

    setShowTimeoutDialog(true);
    setCountdown(Math.max(0, Math.ceil((logoutAt - Date.now()) / 1000)));

    if (countdownInterval) clearInterval(countdownInterval);

    // Calcula o tempo restante a partir do timestamp real
    const interval = setInterval(() => {
      const remaining = Math.ceil((logoutAt - Date.now()) / 1000);
      if (remaining <= 0) {
        clearInterval(interval);
        handleAutoLogout();
      } else {
        setCountdown(remaining);
      }
    }, 500);

    setCountdownInterval(interval);
  };

  /* Executa logout automático quando o contador chega a zero.*/
  const handleAutoLogout = async () => {
    logoutScheduledAt.current = null;
    if (countdownInterval) clearInterval(countdownInterval);
    setShowTimeoutDialog(false);

    /* O redirecionamento será feito automaticamente pelo AuthGuard
     quando o estado isAuthenticated mudar para false no Redux. */
    await logout();
  };

  /* Executado quando o usuário clica em "Continuar sessão".*/
  const handleContinueSession = (external = false) => {
    logoutScheduledAt.current = null;
    if (countdownInterval) clearInterval(countdownInterval);
    setShowTimeoutDialog(false);

    if (!external) {
      // Avisa as outras abas para fecharem o dialog também
      localStorage.setItem('continueSession', Date.now().toString());
      updateLastActivity();
    }
  };

  /* Rastreia abas abertas para encerrar a sessão de login quando a última fechar */
  useEffect(() => {
    const userId = account.user?.id;
    if (!account.isAuthenticated || !userId) return;

    const deviceIdKey = 'device_session_id';
    let deviceId = localStorage.getItem(deviceIdKey);
    if (!deviceId) {
      deviceId = uuidv4();
      localStorage.setItem(deviceIdKey, deviceId);
    }
    const finalDeviceId = deviceId as string;

    const listaAbasKey = `login_tabs_${userId}`;

    const abas = JSON.parse(localStorage.getItem(listaAbasKey) || '[]') as string[];
    if (!abas.includes(loginAbaId)) {
      abas.push(loginAbaId);
      localStorage.setItem(listaAbasKey, JSON.stringify(abas));
    }

    const removerAba = () => {
      const lista = JSON.parse(localStorage.getItem(listaAbasKey) || '[]') as string[];
      const restantes = lista.filter((id) => id !== loginAbaId);
      if (restantes.length === 0) {
        localStorage.removeItem(listaAbasKey);
      } else {
        localStorage.setItem(listaAbasKey, JSON.stringify(restantes));
      }
      return restantes.length;
    };

    let isCleanedUp = false;

    const cleanup = () => {
      if (isCleanedUp) return;
      isCleanedUp = true;

      if (localStorage.getItem('logoutSignal')) {
        removerAba();
        return;
      }

      const remaining = removerAba();
      if (remaining === 0) {
        localStorage.setItem('session_ended', 'true');
        closeSessionLogin({ entityType: 'login', entityId: userId, deviceId: finalDeviceId });
      }
    };

    window.addEventListener('beforeunload', cleanup);
    return () => {
      window.removeEventListener('beforeunload', cleanup);
      cleanup();
    };
  }, [account.isAuthenticated, account.user?.id]);

  /* Verifica se o usuário ultrapassou o tempo máximo de inatividade.*/
  const checkInactivity = () => {
    const lastActivity = Number(localStorage.getItem('lastActivity'));
    if (!lastActivity) return;

    const diff = Date.now() - lastActivity;

    if (diff > TEMPO_INATIVIDADE && !logoutScheduledAt.current) {
      startCountdown();
    }
  };

  // ================================
  // Monitoramento de Eventos
  // ================================

  useEffect(() => {
    if (!account.isAuthenticated || showTimeoutDialog) return;

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

    events.forEach((event) => window.addEventListener(event, updateLastActivity));

    const interval = setInterval(checkInactivity, CHECK_INACTIVITY_INTERVAL);

    return () => {
      events.forEach((event) => window.removeEventListener(event, updateLastActivity));
      clearInterval(interval);
    };
  }, [account.isAuthenticated, showTimeoutDialog]);

  // ================================
  // Verificação ao retornar para a aba
  // ================================

  useEffect(() => {
    if (!account.isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      // Se o logout automático já deveria ter ocorrido enquanto a aba estava em background,
      // executa imediatamente sem esperar o próximo ciclo do setInterval.
      if (logoutScheduledAt.current && Date.now() >= logoutScheduledAt.current) {
        handleAutoLogout();
        return;
      }

      // Verifica inatividade ao retornar para a aba (não aguarda o próximo ciclo do interval)
      if (!logoutScheduledAt.current) {
        checkInactivity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [account.isAuthenticated]);

  // ================================
  // Sincronização entre abas
  // ================================

  useEffect(() => {
    const handleStorageChange = async (event: StorageEvent) => {
      if (event.key === 'logoutSignal' && event.newValue) {
        // Encerra as sessões locais enquanto o token ainda reside em memória (se houver).
        await closeAllRegisteredSessions();
        // Limpa o Redux imediatamente para deslogar a UI e parar os hooks de trackers.
        dispatch(clearUserAuth());
        dispatch(empresaCleanListEmps());

        // Se o Firebase ainda não sincronizou o signOut, força nesta aba para invalidar tokens locais.
        if (firebase.auth().currentUser) {
          await firebase.auth().signOut().catch(() => { });
        }
      }

      // Sincronização do contador de inatividade
      if (event.key === 'activitySync' && event.newValue) {
        if (showTimeoutDialog) {
          handleContinueSession(true);
        }
      }

      // Outra aba iniciou o contador regressivo
      if (event.key === 'countdownStart' && event.newValue) {
        const externalLogoutAt = Number(event.newValue);
        if (!logoutScheduledAt.current || Math.abs(logoutScheduledAt.current - externalLogoutAt) > 2000) {
          startCountdown(externalLogoutAt);
        }
      }

      // Outra aba clicou em "Continuar Sessão"
      if (event.key === 'continueSession' && event.newValue) {
        handleContinueSession(true);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [showTimeoutDialog, countdownInterval]);

  // ================================
  // Firebase Auth
  // ================================

  const signInWithEmailAndPassword = async (
    email: string,
    password: string
  ): Promise<firebase.auth.UserCredential> => {
    sessionStorage.setItem('tab_verified', 'true');
    localStorage.removeItem('session_ended');

    await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);
    return firebase.auth().signInWithEmailAndPassword(email, password);
  };

  const signInWithGoogle = async () => {
    const provider = new firebase.auth.GoogleAuthProvider();
    sessionStorage.setItem('tab_verified', 'true');
    localStorage.removeItem('session_ended');

    await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);
    return firebase.auth().signInWithPopup(provider);
  };

  const createUserWithEmailAndPassword = (email: string, password: string) =>
    firebase.auth().createUserWithEmailAndPassword(email, password);

  const sendPasswordResetEmail = (email: string) => firebase.auth().sendPasswordResetEmail(email);
  const verifyPasswordResetCode = (code: string) => firebase.auth().verifyPasswordResetCode(code);
  const confirmPasswordReset = (code: string, newPassword: string) =>
    firebase.auth().confirmPasswordReset(code, newPassword);

  const loginAbaId = useRef(Math.random().toString(36).substring(2)).current;

  const isInitialAuthCheck = useRef(true);

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(async (usuarioFirebase) => {
      const isFirstRun = isInitialAuthCheck.current;
      isInitialAuthCheck.current = false;

      if (usuarioFirebase) {
        // DETECTOR DE REINÍCIO DE NAVEGADOR
        if (isFirstRun) {
          const isVerified = sessionStorage.getItem('tab_verified') === 'true';
          const sessionEnded = localStorage.getItem('session_ended') === 'true';

          if (!isVerified && sessionEnded) {
            await logout();
            return;
          }
        }

        sessionStorage.setItem('tab_verified', 'true');

        // Limpa o sinal para que sessões futuras iniciem normalmente
        localStorage.removeItem('logoutSignal');
        updateLastActivity();
        try {
          const usuarioFirebaseTokenResult = await usuarioFirebase.getIdTokenResult(true);
          const { empresa, user_id } = usuarioFirebaseTokenResult.claims;
          const selectedEmpresaId = sessionStorage.getItem('selectedEmpresaId') || empresa;

          dispatch(
            userDataRequestInit({
              firebaseId: user_id,
              empId: selectedEmpresaId
            })
          );
        } catch (e: any) {
          enqueueSnackbar(e.mensagem || 'Erro de autenticação', {
            variant: 'error'
          });
        }
      } else {
        dispatch(
          authStateChanged({
            isAuthenticated: false,
            user: null,
            loading: false
          })
        );
      }
    });

    return unsubscribe;
  }, []);

  if (!account.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <>
      <AuthContext.Provider
        value={{
          ...account,
          method: 'FirebaseAuth',
          createUserWithEmailAndPassword,
          signInWithEmailAndPassword,
          signInWithGoogle,
          sendPasswordResetEmail,
          verifyPasswordResetCode,
          confirmPasswordReset,
          logout
        }}>
        {children}
      </AuthContext.Provider>

      <Dialog open={showTimeoutDialog} disableBackdropClick disableEscapeKeyDown>
        <DialogTitle>Sessão prestes a expirar</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Você será desconectado em <strong>{countdown}</strong> segundos por inatividade.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleContinueSession()} color="primary" variant="contained">
            Continuar sessão
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AuthContext;
